import styled from '@emotion/styled'
import { format } from 'date-fns'
import { DayPicker, DayPickerSingleProps } from 'react-day-picker'

import { TickerUp } from '@arcade/web/icons/react'

import { DatePickerCaption } from './DatePickerCaption'
import { DatePickerClassNames } from './DatePickerClassNames'
import { Copy, defaultCopy } from './DatePickerCopy'
import { DatePickerDropdown } from './DatePickerDropdown'
import { DatePickerDropdownContextProvider } from './DatePickerDropdownContext'
import { DatePickerTitleBar } from './DatePickerTitleBar'

const Container = styled.section`
  border-radius: 1rem;
  background-color: ${props => props.theme.colors.elevatedBackground};
  padding: ${props => props.theme.spacing.l};
  gap: ${props => props.theme.spacing.l};
  display: grid;
  border: 1px solid ${props => props.theme.colors.hairline};
  user-select: none;
  grid-template-rows: max-content max-content;
  width: max-content;
  box-shadow: 0px 12px 24px 0px #0000000a;
`

const NavIcon = () => <TickerUp size={24} />

export type DatePickerProps = Omit<
  DayPickerSingleProps & { copy?: Copy; onClose?: () => void },
  | 'captionLayout'
  | 'className'
  | 'classNames'
  | 'components'
  | 'formatters'
  | 'labels'
  | 'locale'
  | 'mode'
  | 'numberOfMonths'
  | 'styles'
>

const getRangeStartFallback = (props: DatePickerProps) => {
  return [props.fromYear, props.fromMonth, props.fromDate].some(value => value != null)
    ? {}
    : { fromYear: new Date().getFullYear() - 5 }
}

const getRangeEndFallback = (props: DatePickerProps) => {
  return [props.toYear, props.toMonth, props.toDate].some(value => value != null)
    ? {}
    : { toYear: new Date().getFullYear() }
}

const DatePicker = ({ onClose, copy = defaultCopy, ...props }: DatePickerProps) => {
  return (
    <Container>
      <DatePickerTitleBar copy={copy} onClose={onClose} />
      <DatePickerDropdownContextProvider>
        {({ close: closeOpenDropdown }) => (
          <DatePickerClassNames>
            {classNames => (
              <DayPicker
                {...props}
                {...getRangeStartFallback(props)}
                {...getRangeEndFallback(props)}
                mode="single"
                locale={copy.locale}
                captionLayout="dropdown-buttons"
                classNames={classNames}
                components={{
                  Caption: DatePickerCaption,
                  Dropdown: DatePickerDropdown,
                  IconRight: NavIcon,
                  IconLeft: NavIcon,
                }}
                formatters={{
                  formatMonthCaption: (date, options) => format(date, 'MMM', { locale: options?.locale }),
                  formatWeekdayName: (date, options) => format(date, 'EEEEE', { locale: options?.locale }),
                }}
                labels={{
                  labelMonthDropdown: () => copy.dropdowns.monthLabel,
                  labelYearDropdown: () => copy.dropdowns.yearLabel,
                  labelNext: () => copy.navigation.next,
                  labelPrevious: () => copy.navigation.previous,
                }}
                onMonthChange={(...args) => {
                  closeOpenDropdown()
                  props.onMonthChange?.(...args)
                }}
              />
            )}
          </DatePickerClassNames>
        )}
      </DatePickerDropdownContextProvider>
    </Container>
  )
}

export default DatePicker
